import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API, imgUrl } from "../lib/api";
import { LINES as STATIC_LINES } from "../lib/site";

const hexToRgba = (hex, alpha) => {
  const m = (hex || "#B8912F").replace("#", "");
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const v = parseInt(full, 16);
  return `rgba(${(v >> 16) & 255},${(v >> 8) & 255},${v & 255},${alpha})`;
};

const FALLBACK_LINES = Object.entries(STATIC_LINES).map(([id, l], i) => ({
  id,
  name: l.name,
  color: l.color,
  description: l.desc,
  banner: l.banner,
  order: i,
}));

export const DEFAULT_CONTENT = {
  logo: "",
  hero_image: "",
  about_image: "",
  quality_image: "",
  app_image: "",
  whatsapp_number: "18295067111",
  hero: {
    overline: "Distribuidor oficial · República Dominicana",
    title1: "INNOVACIÓN QUE",
    title2: "TRANSFORMA",
    title3: "LA SALUD",
    subtitle:
      "Soluciones farmacéuticas de alta pureza y rendimiento. Distribución oficial autorizada en Santo Domingo, Santiago, Punta Cana y todo el territorio dominicano.",
  },
  stats: [
    { value: "100%", label: "Pureza Farmacéutica Garantizada" },
    { value: "24/48h", label: "Envíos a Nivel Nacional" },
    { value: "4 Líneas", label: "Landerlan · Silver · Gold · Diamond" },
    { value: "QR", label: "Sistema de Autenticidad" },
  ],
  marquee: [
    "LANDERLAN DOMINICANA",
    "DISTRIBUIDOR AUTORIZADO",
    "REPÚBLICA DOMINICANA",
    "LÍNEA LANDERLAN",
    "SILVER SERIES",
    "GOLD SERIES",
    "DIAMOND SERIES",
    "AUTENTICIDAD GARANTIZADA",
    "ENVÍOS A SANTO DOMINGO · SANTIAGO · PUNTA CANA",
  ],
  lines_intro: {
    overline: "Nuestras líneas",
    title: "CUATRO LÍNEAS, UN MISMO ESTÁNDAR DE PUREZA",
    subtitle: "Seleccione una línea para explorar formulaciones especializadas de alta exigencia.",
  },
  line_descs: {
    landerlan: "Cuidado integral del día a día con formulaciones confiables y eficacia comprobada.",
    silver: "Rendimiento constante y bienestar sostenido con precisión farmacéutica y excelente tolerabilidad.",
    gold: "Alto rendimiento, vitalidad y bienestar integral con alta pureza y biodisponibilidad.",
    diamond: "El máximo nivel de excelencia: pureza, tecnología y rigor farmacéutico premium.",
  },
  catalog_intro: {
    overline: "Calidad farmacéutica a tu alcance",
    title: "CATÁLOGO OFICIAL DE PRODUCTOS",
    subtitle:
      "Explore nuestra línea completa de medicamentos y suplementos, desarrollados bajo estrictos estándares de seguridad y eficacia.",
  },
  app_section: {
    title: "TU BIENESTAR, AL ALCANCE DE TUS MANOS",
    text: "Acceda fácilmente a nuestro catálogo completo, con información actualizada de todos nuestros productos. Nuestra app brinda una experiencia ágil y segura, diseñada para profesionales y pacientes que priorizan la confianza en cada decisión de salud.",
  },
  quality: {
    overline: "Responsabilidad y compromiso social",
    title: "CREDIBILIDAD Y CALIDAD EN PRO DE LA SALUD",
    text: "En Landerlan Dominicana trabajamos con los más altos estándares de calidad, innovación y responsabilidad, garantizando productos seguros y efectivos que respaldan la confianza de profesionales de la salud y pacientes.",
    highlights: [
      { title: "Sellos holográficos", desc: "Cada empaque cuenta con sellos de seguridad holográficos inviolables contra la falsificación." },
      { title: "QR único por frasco", desc: "Código QR individual por frasco y caja para validar lote, origen y cadena de distribución." },
      { title: "Certificación GMP", desc: "Manufactura bajo Buenas Prácticas (GMP) con análisis de pureza independiente por lote." },
    ],
  },
  about: {
    overline: "Nosotros",
    title: "COMPROMISO CON REPÚBLICA DOMINICANA",
    text: "En Landerlan Dominicana desarrollamos y distribuimos soluciones farmacéuticas de alta calidad, impulsados por tecnología de vanguardia y un firme compromiso con el bienestar de las personas en Santo Domingo, Santiago, Punta Cana y todo el país.",
    pillars: [
      { title: "Misión", desc: "Distribuir soluciones farmacéuticas de alta pureza en toda República Dominicana, con tecnología de vanguardia y un firme compromiso con el bienestar de las personas." },
      { title: "Visión", desc: "Ser el distribuidor farmacéutico de referencia en el Caribe, reconocido por la autenticidad, trazabilidad y excelencia de cada producto que entregamos." },
      { title: "Compromiso social", desc: "Promovemos la salud responsable apoyando a atletas, profesionales médicos y la comunidad dominicana con transparencia y educación." },
    ],
  },
  contact: {
    overline: "Contacto y pedidos",
    title: "ATENCIÓN DIRECTA PARA FARMACIAS Y CLIENTES EN RD",
    subtitle:
      "Atención personalizada para farmacias, distribuidores y clientes en toda República Dominicana. Escriba ahora y un asesor le responderá de inmediato.",
    address: "Av. Winston Churchill, Santo Domingo, República Dominicana",
    phone: "+1 (829) 506-7111",
    schedule: "Lun–Vie: 8:00 AM – 6:00 PM · Sáb: 9:00 AM – 1:00 PM",
    email: "contacto@landerlandominicana.com",
  },
  footer_legal:
    "Aviso legal: los productos farmacéuticos presentados en este sitio requieren prescripción y supervisión de un profesional de la salud. La información mostrada no sustituye la orientación médica. Verifique siempre la autenticidad del producto mediante su código QR oficial.",
};

const ContentContext = createContext({ ...DEFAULT_CONTENT, lines: FALLBACK_LINES, getLine: (id) => FALLBACK_LINES.find((l) => l.id === id) });

export const useContent = () => useContext(ContentContext);

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [lines, setLines] = useState(FALLBACK_LINES);

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then((res) => {
        const merged = { ...DEFAULT_CONTENT, ...res.data };
        setContent(merged);
        if (merged.logo) {
          const link = document.querySelector('link[rel="icon"]');
          if (link) link.href = imgUrl(merged.logo);
        }
      })
      .catch(() => {});
    axios
      .get(`${API}/lines`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setLines(res.data);
      })
      .catch(() => {});
  }, []);

  const getLine = (id) => {
    const l = lines.find((x) => x.id === id);
    if (!l)
      return {
        id,
        name: id,
        color: "#B8912F",
        badgeBg: "rgba(184,145,47,0.12)",
        badgeBorder: "rgba(184,145,47,0.45)",
        description: "",
        banner: "",
      };
    return { ...l, badgeBg: hexToRgba(l.color, 0.12), badgeBorder: hexToRgba(l.color, 0.45) };
  };

  return (
    <ContentContext.Provider value={{ ...content, lines, getLine }}>
      {children}
    </ContentContext.Provider>
  );
}
